import React from "react";
import {
  TextField,
  type TextFieldProps as AriaTextFieldProps,
  type ValidationResult,
} from "react-aria-components";
import { tv, type VariantProps } from "tailwind-variants";

import { cn } from "@/lib/helpers/cn";

import { FieldError2 } from "../atoms/FieldError";
import { Input2, type Input2Props } from "../atoms/Input";
import { Label2 } from "../atoms/Label";
import { Text2 } from "../atoms/Text";
import { TextArea2, type TextArea2Props } from "../atoms/TextArea";

type BaseTextFieldProps = {
  className?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  isReadOnly?: boolean;
  isRequired?: boolean;
  label: string;
  hasStrongLabel?: boolean;
} & AriaTextFieldProps &
  VariantProps<typeof inputStyles>;

type TextFieldInputProps = BaseTextFieldProps & {
  mode?: "input";
  textAreaProps?: never;
  textInputProps?: Input2Props;
};

type TextFieldTextAreaProps = BaseTextFieldProps & {
  mode: "textarea";
  textAreaProps?: TextArea2Props;
  textInputProps?: never;
};

export type TextFieldProps = TextFieldInputProps | TextFieldTextAreaProps;

// TODO: Pass sizes to input
export function TextField2(props: TextFieldProps) {
  const {
    className,
    description,
    errorMessage,
    hasStrongLabel,
    isInvalid,
    isReadOnly,
    isRequired,
    label,
    mode = "input",
    textAreaProps: { className: classNameArea, ...otherAreaProps } = {},
    textInputProps: { className: classNameInput, ...otherInputProps } = {},
    ...otherProps
  } = props;

  const { field } = inputStyles();
  const variant = isReadOnly ? "readOnly" : "default";

  return (
    <TextField isInvalid={isInvalid} className={cn(field(), className)} {...otherProps}>
      <Label2
        className={hasStrongLabel ? "tw:font-bold" : ""}
        label={label}
        isRequired={isRequired}
        isInvalid={isInvalid}
      />

      {mode === "input" && (
        <Input2 className={classNameInput} variant={variant} {...otherInputProps} />
      )}

      {mode === "textarea" && (
        <TextArea2 className={classNameArea} variant={variant} {...otherAreaProps} />
      )}

      <FieldError2 errorMessage={errorMessage} />
      {description && !isInvalid && <Text2 text={description} slot="description" />}
    </TextField>
  );
}

export const inputStyles = tv({
  defaultVariants: {
    size: "md",
  },
  slots: {
    field: "tw:flex tw:flex-col tw:gap-1.5",
  },
});
